import './styles.scss';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { BdsIcon, BdsTypo } from 'blip-ds/dist/blip-ds-react';
import { trackEvent } from '../../../../api/Billing/Tracking';
import { useTenant } from './../../../../contexts/TenantContext';
import { useCommon } from './../../../../contexts/CommonContext';
import { origin, action } from '../../../../shared/SegmentEvents';
import { localization as asideMenuLocalization } from './localization';
import { useContentLocalizer } from '../../../../hooks/useContentLocalizer';
import { BillingMetrics, MetricStatus } from '../../../../shared/BillingMetrics';
import { useMiuConsumptionContext } from '../../../../contexts/Billing/MiuConsumptionContext';
import { useMeuConsumptionContext } from '../../../../contexts/Billing/MeuConsumptionContext';
import { useBillingMetricsContext } from '../../../../contexts/Billing/BillingMetricsContext';
import { billingRoutesTracks, billingSubRoutesTracks } from '../../../../routes/BillingRoutes';
import { useBillingPlanUsageContext } from '../../../../contexts/Billing/BillingPlanUsageContext';
import { useBillingConsumptionContext } from '../../../../contexts/Billing/BillingConsumptionContext';
import { useAdvancedDeuConsumptionContext } from '../../../../contexts/Billing/AdvancedDeuConsumptionContext';

export const AsideMenu: React.FC = ({ children }) => {
  const { planStatus } = useBillingPlanUsageContext();
  const { dateToSearch } = useBillingConsumptionContext();
  const { hasDuplicatedExchangedMessage } = useBillingMetricsContext();
  const { miuMetricIds, miuStatus, localization } = useMiuConsumptionContext();
  const { meuMetricIds, meuStatus, localizationMeu } = useMeuConsumptionContext();
  const { advancedDeuMetricIds } = useAdvancedDeuConsumptionContext();

  const content = useContentLocalizer(asideMenuLocalization);

  const history = useHistory();
  const location = useLocation();
  const [currentPath, setCurrentPath] = useState(history.location.pathname);
  const [countTrack, setCountTrack] = useState(0);

  const {
    tenant,
    tenant: { privateRoutes },
  } = useTenant();
  const {
    common: { loggedUser },
  } = useCommon();

  const isExternalAccessRoute = history.action === 'POP';

  const trackingProps = {
    date: dateToSearch,
    planStatus: planStatus.current,
    origin: origin.ASIDEMENU,
    action: action.CLICK,
  };

  const routes = [...billingRoutesTracks, ...billingSubRoutesTracks];

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isExternalAccessRoute) {
        const trackingKey = routes.find((metric) => metric.path === currentPath);
        if (trackingKey && countTrack < 1) {
          trackEvent(trackingKey.segmentEvent, tenant, loggedUser, {
            ...trackingProps,
            origin: origin.EXTERNAL,
            action: action.OPEN,
          });
          setCountTrack(1);
        }
      }
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [planStatus.current]);

  useEffect(() => {
    setCurrentPath(location.pathname);
  }, [location]);

  const goTo = (path: string) => {
    history.push(path);
  };

  let allowedAsideMenuMetrics = privateRoutes.filter((item) => item.group === 'asideMenu');

  const haveMiuMSG = miuMetricIds.includes(BillingMetrics.exchangedMessage);
  const existMiu = miuMetricIds.includes(BillingMetrics.monthlyActiveUser);

  const haveMeuMSG = meuMetricIds.includes(BillingMetrics.exchangedMessage);
  const existMeu = meuMetricIds.includes(BillingMetrics.monthlyEngagedUser);

  const showSingleMsg = hasDuplicatedExchangedMessage?.(miuMetricIds, meuMetricIds);
  
  const showMiu = ((haveMiuMSG && miuMetricIds.length > 1) || existMiu);
  const showMeu = ((haveMeuMSG && meuMetricIds.length > 1) || existMeu);

  if (!showSingleMsg && haveMiuMSG && !showMiu) {
    allowedAsideMenuMetrics = allowedAsideMenuMetrics.filter(
      (item) => item.option !== 'mauConsumption'
    );
  }

  if (!showSingleMsg && haveMeuMSG && !showMeu) {
    allowedAsideMenuMetrics = allowedAsideMenuMetrics.filter(
      (item) => item.option !== 'meuConsumption'
    );
  }

  if (showSingleMsg) {
    allowedAsideMenuMetrics = allowedAsideMenuMetrics.filter(
      (item) => item.option !== 'meuConsumption'
    );
  }

  const hasAdvancedDeuMetrics =
    allowedAsideMenuMetrics.some((item) => item.option === 'advancedDeuConsumption');

  if (hasAdvancedDeuMetrics) {
    allowedAsideMenuMetrics = allowedAsideMenuMetrics.filter(
      (item) => item.option !== 'deuConsumption'
    );
  }

  return (
    <div className="main-container">
      <div className="main-left-column">
        <ol className="main-side-nav-menuList">
          {allowedAsideMenuMetrics.map((item) => (
            <li
              onClick={() => trackEvent(item.segmentEvent, tenant, loggedUser, trackingProps)}
              key={item.option}
              id={item.option}
              className={item.path === currentPath ? 'active' : ''}
            >
              <div
                className="side-menu-item-anchor"
                onClick={() => goTo(item.path)}
                onKeyDown={(e) => e.key === 'Enter' && goTo(item.path)}
                tabIndex={0}
                role="button"
                aria-label={`Navigate to ${item.option}`}
              >
                <div className="side-menu-item-icon">
                  <BdsIcon name={item.icon} size="large"></BdsIcon>
                </div>
                <div className="side-menu-item-text">
                  <BdsTypo variant="fs-14" line-height="plus" bold="bold">
                  {(() => {
                    if (item.option === 'mauConsumption') {
                    return content[item.group][localization.asideMenuLocalization].title;
                    } else if (item.option === 'meuConsumption') {
                    return content[item.group][localizationMeu.asideMenuLocalization].title;
                    } else {
                    return content[item.group][item.option].title;
                    }
                  })()}
                  </BdsTypo>
                  <BdsTypo variant="fs-12" line-height="plus" tag="span" bold="regular">
                  {(() => {
                    if (item.option === 'mauConsumption') {
                    return content[item.group][localization.asideMenuLocalization].subtitle;
                    } else if (item.option === 'meuConsumption') {
                    return content[item.group][localizationMeu.asideMenuLocalization].subtitle;
                    } else {
                    return content[item.group][item.option].subtitle;
                    }
                  })()}
                  </BdsTypo>
                </div>
              </div>
              <div className="side-menu-item-border-bottom"></div>
            </li>
          ))}
        </ol>
      </div>
      <div className="main-base-template-content">{children}</div>
    </div>
  );
};
